import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { Modal, ModalHeader, ModalBody, ModalFooter, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';
import {
  closeModal,
  openModal,
  getEdges,
  getNodes,
  getWorkflow,
  getWorkflowState,
  saveWorkflowDraftRequest,
  getWorkflowHasUnsavedChanges,
} from '../../../ducks';
import { CLONE_JOURNEY_MODAL, CLONE_HAS_UNSAVED_CHANGES_MODAL } from '../../../constants';

export const DONT_SAVE_BUTTON_TEST_ID = 'dont-save-button';
export const SAVE_AND_SWITCH_BUTTON_TEST_ID = 'save-and-switch-button';

export interface CloneHasUnsavedChangesModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
}

export const CloneHasUnsavedChangesModal: React.FC<
  React.PropsWithChildren<CloneHasUnsavedChangesModalProps>
> = ({ isOpen }) => {
  const dispatch = useDispatch();
  const workflowState = useSelector(getWorkflowState);
  const hasUnsavedChanges = useSelector(getWorkflowHasUnsavedChanges);
  const workflow = useSelector(getWorkflow);
  const nodes = useSelector(getNodes);
  const edges = useSelector(getEdges);

  const [isSaving, setIsSaving] = useState(false);

  const handleCloseModal = () => dispatch(closeModal(CLONE_HAS_UNSAVED_CHANGES_MODAL));

  const handleOpenCloneModal = () => {
    handleCloseModal();
    dispatch(openModal(CLONE_JOURNEY_MODAL, {}));
  };

  const handleSaveAndOpenCloneModal = () => {
    setIsSaving(true);
    dispatch(saveWorkflowDraftRequest({ ...workflow, nodes, edges }));
  };

  useEffect(() => {
    // when workflow is done saving...
    if (isSaving && !workflowState?.saving) {
      // check if changes were saved and open clone modal
      if (!hasUnsavedChanges) handleOpenCloneModal();
      // or close the prompt if there's an error
      else handleCloseModal();
    }
  }, [isSaving, workflowState, hasUnsavedChanges]);

  return (
    <Modal
      id={CLONE_HAS_UNSAVED_CHANGES_MODAL}
      isModalOpen={isOpen}
      onClose={handleCloseModal}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.WARNING}
    >
      <ModalHeader autoFocus={false}>Unsaved changes</ModalHeader>
      <ModalBody>
        <p>
          This journey has been edited. Do you want to save your changes before copying the journey?
        </p>
      </ModalBody>
      <ModalFooter>
        <Button
          data-test={DONT_SAVE_BUTTON_TEST_ID}
          onClick={handleOpenCloneModal}
          size={BUTTON_SIZES.LARGE}
          variant={BUTTON_VARIANTS.SECONDARY}
        >
          Copy without saving
        </Button>
        <Button
          data-test={SAVE_AND_SWITCH_BUTTON_TEST_ID}
          onClick={handleSaveAndOpenCloneModal}
          size={BUTTON_SIZES.LARGE}
          variant={BUTTON_VARIANTS.PRIMARY}
        >
          Save and copy
        </Button>
      </ModalFooter>
    </Modal>
  );
};
